/**
 * P1-C · Biometric Attendance Console.
 *
 * Route: /admin/attendance/biometric
 * Perm:  biometric:device:manage
 *
 * Three tabs (locked contract):
 *   1. Devices       — vendor-neutral master (create, edit, status flip, rotate secret)
 *   2. Mappings      — employee ↔ (device, biometric_user_id) + bulk CSV Dry-Run → Commit
 *   3. Unmapped Inbox — quarantine review · resolve (explicit reprocess) / reject
 *
 * Contract guardrails:
 *   • No auto-reprocessing — after a mapping create for a (device, bio_user_id)
 *     that has pending unmapped events, the UI shows an EXPLICIT prompt
 *     "Reprocess N pending event(s)?" — never automatic.
 *   • Retiring a device requires a reason ≥ 10 chars; cascade-suspends
 *     active mappings on that device.
 *   • Reject reason ≥ 10 chars.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    ArrowsClockwise, CheckCircle, CloudArrowUp, DeviceMobile, DownloadSimple,
    FileCsv, Info, Key, MagnifyingGlass, PlusCircle, Trash, UserCircle,
    Warning, WarningCircle, XCircle,
} from "@phosphor-icons/react";

import { api, API_BASE, formatApiError, getToken } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
    DialogDescription,
} from "../../components/ui/dialog";


// ─────────────────────────────────────────────────────────────────
// Constants & helpers
// ─────────────────────────────────────────────────────────────────

const DEVICE_STATUS_STYLES = {
    active:      "bg-emerald-100 text-emerald-800 border-emerald-300",
    maintenance: "bg-amber-100 text-amber-800 border-amber-300",
    retired:     "bg-rose-100 text-rose-800 border-rose-300",
};

const MAPPING_STATUS_STYLES = {
    active:    "bg-emerald-100 text-emerald-800 border-emerald-300",
    suspended: "bg-amber-100 text-amber-800 border-amber-300",
    revoked:   "bg-slate-100 text-slate-600 border-slate-300",
};

const RESOLUTION_STATUS_STYLES = {
    held_unmapped:    "bg-amber-100 text-amber-800 border-amber-300",
    held_maintenance: "bg-sky-100 text-sky-800 border-sky-300",
    resolved:         "bg-emerald-100 text-emerald-800 border-emerald-300",
    rejected:         "bg-rose-100 text-rose-800 border-rose-300",
};

const SEV_STYLES = {
    block: "bg-rose-50 border-rose-200 text-rose-700",
    warn:  "bg-amber-50 border-amber-200 text-amber-800",
    info:  "bg-slate-50 border-slate-200 text-slate-700",
};

function StatusBadge({ value, styles, testId }) {
    if (!value) return <span className="text-xs text-slate-400">—</span>;
    return (
        <span className={`inline-flex rounded-md border px-2 py-0.5 text-[11px] font-medium ${styles[value] || "bg-slate-100 text-slate-700 border-slate-300"}`}
            data-testid={testId}>
            {value}
        </span>
    );
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}


// ─────────────────────────────────────────────────────────────────
// Tab 1 · Devices
// ─────────────────────────────────────────────────────────────────

function DevicesTab({ devices, loading, onRefresh }) {
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [creating, setCreating] = useState(false);
    const [statusFlip, setStatusFlip] = useState(null);   // {device, newStatus}
    const [rotate, setRotate] = useState(null);           // device

    const filtered = useMemo(() => {
        const s = search.trim().toLowerCase();
        return devices.filter((d) => {
            if (statusFilter && d.status !== statusFilter) return false;
            if (!s) return true;
            return (d.device_code || "").toLowerCase().includes(s)
                || (d.name || "").toLowerCase().includes(s)
                || (d.location_name || "").toLowerCase().includes(s);
        });
    }, [devices, search, statusFilter]);

    return (
        <div className="space-y-4" data-testid="tab-devices">
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[240px] max-w-md">
                    <MagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)}
                        className="pl-9" placeholder="Search code / name / location…"
                        data-testid="devices-search" />
                </div>
                <select className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm"
                    value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    data-testid="devices-status-filter">
                    <option value="">All statuses</option>
                    <option value="active">Active</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="retired">Retired</option>
                </select>
                <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}
                    data-testid="devices-refresh">
                    <ArrowsClockwise className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                </Button>
                <div className="ml-auto">
                    <Button size="sm" onClick={() => setCreating(true)}
                        data-testid="btn-create-device">
                        <PlusCircle className="h-4 w-4 mr-1.5" /> New device
                    </Button>
                </div>
            </div>

            <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                        <tr>
                            <th className="text-left px-3 py-2">Device code</th>
                            <th className="text-left px-3 py-2">Name / location</th>
                            <th className="text-left px-3 py-2">Vendor</th>
                            <th className="text-left px-3 py-2">Ingest mode</th>
                            <th className="text-left px-3 py-2">Status</th>
                            <th className="text-left px-3 py-2">Last seen</th>
                            <th className="text-right px-3 py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody data-testid="devices-tbody">
                        {filtered.length === 0 && (
                            <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500 text-sm">
                                No devices to display.
                            </td></tr>
                        )}
                        {filtered.map((d) => (
                            <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50"
                                data-testid={`device-row-${d.device_code}`}>
                                <td className="px-3 py-2 font-mono text-xs">{d.device_code}</td>
                                <td className="px-3 py-2">
                                    <div className="font-medium">{d.name}</div>
                                    <div className="text-xs text-slate-500">{d.location_name || "—"}</div>
                                </td>
                                <td className="px-3 py-2 text-xs text-slate-600">{d.vendor || "—"}</td>
                                <td className="px-3 py-2 text-xs">
                                    <Badge variant="outline" className="text-[10px]">{d.ingest_mode}</Badge>
                                </td>
                                <td className="px-3 py-2">
                                    <StatusBadge value={d.status} styles={DEVICE_STATUS_STYLES}
                                        testId={`device-status-${d.device_code}`} />
                                </td>
                                <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(d.last_seen_at)}</td>
                                <td className="px-3 py-2 text-right">
                                    <div className="inline-flex gap-1">
                                        {d.status !== "retired" && (
                                            <>
                                                {d.status !== "active" && (
                                                    <Button variant="outline" size="sm"
                                                        onClick={() => setStatusFlip({ device: d, newStatus: "active" })}
                                                        data-testid={`btn-activate-${d.device_code}`}>
                                                        <CheckCircle className="h-3.5 w-3.5" />
                                                    </Button>
                                                )}
                                                {d.status !== "maintenance" && (
                                                    <Button variant="outline" size="sm"
                                                        onClick={() => setStatusFlip({ device: d, newStatus: "maintenance" })}
                                                        data-testid={`btn-maintenance-${d.device_code}`}>
                                                        Maint
                                                    </Button>
                                                )}
                                                <Button variant="outline" size="sm"
                                                    onClick={() => setStatusFlip({ device: d, newStatus: "retired" })}
                                                    data-testid={`btn-retire-${d.device_code}`}>
                                                    Retire
                                                </Button>
                                            </>
                                        )}
                                        <Button variant="outline" size="sm" onClick={() => setRotate(d)}
                                            data-testid={`btn-rotate-${d.device_code}`}>
                                            <Key className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {creating && (
                <CreateDeviceDialog
                    open={creating}
                    onClose={() => setCreating(false)}
                    onCreated={() => { setCreating(false); onRefresh(); }}
                />
            )}
            {statusFlip && (
                <StatusFlipDialog
                    device={statusFlip.device} newStatus={statusFlip.newStatus}
                    onClose={() => setStatusFlip(null)}
                    onDone={() => { setStatusFlip(null); onRefresh(); }}
                />
            )}
            {rotate && (
                <RotateSecretDialog
                    device={rotate}
                    onClose={() => setRotate(null)}
                    onDone={() => { setRotate(null); onRefresh(); }}
                />
            )}
        </div>
    );
}


function CreateDeviceDialog({ open, onClose, onCreated }) {
    const [form, setForm] = useState({
        device_code: "", name: "", vendor: "", model: "",
        location_name: "", ingest_mode: "shared_token", ingest_secret: "",
    });
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        const payload = { ...form };
        if (!/^[A-Z][A-Z0-9_-]{1,63}$/.test(payload.device_code)) {
            toast.error("device_code must be uppercase letters/digits/_/- and start with a letter.");
            return;
        }
        if (payload.ingest_secret && payload.ingest_secret.length < 16) {
            toast.error("Ingest secret must be at least 16 characters.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/attendance/biometric/devices", payload);
            toast.success(`Device ${payload.device_code} created.`);
            onCreated();
        } catch (err) {
            toast.error(formatApiError(err) || "Create failed");
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-lg" data-testid="dialog-create-device">
                <DialogHeader>
                    <DialogTitle>New biometric device</DialogTitle>
                    <DialogDescription>
                        Register a physical terminal. Device code must be uppercase.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-3">
                    <div>
                        <Label>Device code *</Label>
                        <Input value={form.device_code}
                            onChange={(e) => setForm({ ...form, device_code: e.target.value.toUpperCase() })}
                            placeholder="e.g. BIO-HQ-01" data-testid="input-device-code" />
                    </div>
                    <div>
                        <Label>Display name *</Label>
                        <Input value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="input-device-name" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Vendor</Label>
                            <Input value={form.vendor}
                                onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                                data-testid="input-device-vendor" />
                        </div>
                        <div>
                            <Label>Model</Label>
                            <Input value={form.model}
                                onChange={(e) => setForm({ ...form, model: e.target.value })}
                                data-testid="input-device-model" />
                        </div>
                    </div>
                    <div>
                        <Label>Location</Label>
                        <Input value={form.location_name}
                            onChange={(e) => setForm({ ...form, location_name: e.target.value })}
                            data-testid="input-device-location" />
                    </div>
                    <div>
                        <Label>Ingest mode</Label>
                        <select className="h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm"
                            value={form.ingest_mode}
                            onChange={(e) => setForm({ ...form, ingest_mode: e.target.value })}
                            data-testid="select-ingest-mode">
                            <option value="shared_token">shared_token</option>
                            <option value="device_secret">device_secret</option>
                        </select>
                    </div>
                    {form.ingest_mode === "device_secret" && (
                        <div>
                            <Label>Device secret (≥ 16 chars) *</Label>
                            <Input value={form.ingest_secret} type="password"
                                onChange={(e) => setForm({ ...form, ingest_secret: e.target.value })}
                                data-testid="input-device-secret" />
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving || !form.device_code || !form.name}
                        data-testid="btn-submit-device">
                        {saving ? "Creating…" : "Create"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function StatusFlipDialog({ device, newStatus, onClose, onDone }) {
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const requiresReason = newStatus === "retired";

    const submit = async () => {
        if (requiresReason && reason.trim().length < 10) {
            toast.error("Retirement reason must be at least 10 characters.");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/attendance/biometric/devices/${device.id}/status`, {
                status: newStatus, reason: reason.trim(),
            });
            toast.success(`Device ${device.device_code} → ${newStatus}.`);
            onDone();
        } catch (err) {
            toast.error(formatApiError(err) || "Status flip failed");
        } finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent data-testid="dialog-device-status">
                <DialogHeader>
                    <DialogTitle>Change device status → {newStatus}</DialogTitle>
                    <DialogDescription>
                        {newStatus === "retired"
                            ? `Retiring ${device.device_code} auto-suspends every active mapping on this device.`
                            : `Device ${device.device_code} will move to ${newStatus}.`}
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-3">
                    <div>
                        <Label>Reason {requiresReason ? "(≥ 10 chars)" : "(optional)"}</Label>
                        <Textarea rows={3} value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            data-testid="input-status-reason" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} data-testid="btn-submit-status">
                        {saving ? "Applying…" : "Apply"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function RotateSecretDialog({ device, onClose, onDone }) {
    const [secret, setSecret] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (secret.length < 16) {
            toast.error("Secret must be at least 16 characters.");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/attendance/biometric/devices/${device.id}/rotate-secret`, {
                new_secret: secret,
            });
            toast.success(`Secret rotated for ${device.device_code}. Ingest mode is now device_secret.`);
            onDone();
        } catch (err) {
            toast.error(formatApiError(err) || "Rotate secret failed");
        } finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent data-testid="dialog-rotate-secret">
                <DialogHeader>
                    <DialogTitle>Rotate device secret · {device.device_code}</DialogTitle>
                    <DialogDescription>
                        Set a new device secret. This also flips the device into
                        <code className="px-1">device_secret</code> ingest mode.
                    </DialogDescription>
                </DialogHeader>
                <div>
                    <Label>New secret (≥ 16 chars)</Label>
                    <Input type="password" value={secret}
                        onChange={(e) => setSecret(e.target.value)}
                        data-testid="input-new-secret" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} data-testid="btn-submit-rotate">
                        {saving ? "Rotating…" : "Rotate"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// ─────────────────────────────────────────────────────────────────
// Tab 2 · Mappings
// ─────────────────────────────────────────────────────────────────

function MappingsTab({ devices, onGlobalRefresh }) {
    const [deviceFilter, setDeviceFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [importing, setImporting] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 500 };
            if (deviceFilter) params.device_id = deviceFilter;
            if (statusFilter) params.status = statusFilter;
            const r = await api.get("/attendance/biometric/mappings", { params });
            setRows(r.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err) || "Failed to load mappings");
        } finally { setLoading(false); }
    }, [deviceFilter, statusFilter]);

    useEffect(() => { refresh(); }, [refresh]);

    return (
        <div className="space-y-4" data-testid="tab-mappings">
            <div className="flex flex-wrap items-center gap-3">
                <select className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm"
                    value={deviceFilter} onChange={(e) => setDeviceFilter(e.target.value)}
                    data-testid="mappings-device-filter">
                    <option value="">All devices</option>
                    {devices.map((d) => (
                        <option key={d.id} value={d.id}>{d.device_code} · {d.name}</option>
                    ))}
                </select>
                <select className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm"
                    value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    data-testid="mappings-status-filter">
                    <option value="">All statuses</option>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="revoked">Revoked</option>
                </select>
                <Button variant="outline" size="sm" onClick={refresh} disabled={loading}
                    data-testid="mappings-refresh">
                    <ArrowsClockwise className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                </Button>
                <div className="ml-auto flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setImporting(true)}
                        data-testid="btn-open-bulk-import">
                        <FileCsv className="h-4 w-4 mr-1.5" /> Bulk import CSV
                    </Button>
                    <Button size="sm" onClick={() => setCreating(true)}
                        data-testid="btn-create-mapping">
                        <PlusCircle className="h-4 w-4 mr-1.5" /> New mapping
                    </Button>
                </div>
            </div>

            <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                        <tr>
                            <th className="text-left px-3 py-2">Device</th>
                            <th className="text-left px-3 py-2">Biometric user id</th>
                            <th className="text-left px-3 py-2">Employee</th>
                            <th className="text-left px-3 py-2">Status</th>
                            <th className="text-left px-3 py-2">Enrolled</th>
                            <th className="text-right px-3 py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody data-testid="mappings-tbody">
                        {rows.length === 0 && (
                            <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500 text-sm">
                                No mappings to display.
                            </td></tr>
                        )}
                        {rows.map((m) => (
                            <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50"
                                data-testid={`mapping-row-${m.id}`}>
                                <td className="px-3 py-2 font-mono text-xs">{m.device_code}</td>
                                <td className="px-3 py-2 font-mono text-xs">{m.biometric_user_id}</td>
                                <td className="px-3 py-2">
                                    <div className="font-medium">{m.employee_full_name || "—"}</div>
                                    <div className="text-xs text-slate-500">{m.employee_code || m.employee_id}</div>
                                </td>
                                <td className="px-3 py-2">
                                    <StatusBadge value={m.status} styles={MAPPING_STATUS_STYLES}
                                        testId={`mapping-status-${m.id}`} />
                                </td>
                                <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(m.enrolled_at)}</td>
                                <td className="px-3 py-2 text-right">
                                    {m.status === "active" && (
                                        <MappingStatusButton mapping={m} to="suspended"
                                            onDone={refresh} />
                                    )}
                                    {m.status === "suspended" && (
                                        <MappingStatusButton mapping={m} to="active" onDone={refresh} />
                                    )}
                                    {m.status !== "revoked" && (
                                        <MappingStatusButton mapping={m} to="revoked"
                                            onDone={refresh} />
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {creating && (
                <CreateMappingDialog
                    devices={devices}
                    onClose={() => setCreating(false)}
                    onCreated={() => {
                        setCreating(false);
                        refresh();
                        onGlobalRefresh();
                    }}
                />
            )}
            {importing && (
                <BulkMappingImportDialog
                    onClose={() => setImporting(false)}
                    onCommitted={() => { setImporting(false); refresh(); onGlobalRefresh(); }}
                />
            )}
        </div>
    );
}


function MappingStatusButton({ mapping, to, onDone }) {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const requires = to === "revoked";

    const submit = async () => {
        if (requires && reason.trim().length < 10) {
            toast.error("Reason must be at least 10 characters.");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/attendance/biometric/mappings/${mapping.id}/status`, {
                status: to, reason: reason.trim(),
            });
            toast.success(`Mapping → ${to}`);
            setOpen(false);
            onDone();
        } catch (err) {
            toast.error(formatApiError(err) || "Failed");
        } finally { setSaving(false); }
    };

    const label = to === "suspended" ? "Suspend" : to === "revoked" ? "Revoke" : "Reactivate";
    return (
        <>
            <Button variant="outline" size="sm" onClick={() => setOpen(true)}
                data-testid={`btn-mapping-${to}-${mapping.id}`} className="ml-1">
                {label}
            </Button>
            {open && (
                <Dialog open onOpenChange={setOpen}>
                    <DialogContent data-testid={`dialog-mapping-${to}`}>
                        <DialogHeader>
                            <DialogTitle>{label} mapping</DialogTitle>
                            <DialogDescription>
                                {mapping.device_code} · biometric_user_id {mapping.biometric_user_id}
                                {" · "} {mapping.employee_full_name || mapping.employee_id}
                            </DialogDescription>
                        </DialogHeader>
                        <div>
                            <Label>Reason {requires ? "(≥ 10 chars)" : "(optional)"}</Label>
                            <Textarea rows={3} value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                data-testid="input-mapping-status-reason" />
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                            <Button onClick={submit} disabled={saving}
                                data-testid="btn-submit-mapping-status">
                                {saving ? "Applying…" : "Apply"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
}


function CreateMappingDialog({ devices, onClose, onCreated }) {
    const [form, setForm] = useState({
        device_id: "", employee_query: "", employee_id: "",
        biometric_user_id: "", notes: "",
    });
    const [empResults, setEmpResults] = useState([]);
    const [saving, setSaving] = useState(false);
    const [reprocessPrompt, setReprocessPrompt] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const q = form.employee_query.trim();
        if (q.length < 2) { setEmpResults([]); return; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get("/hr/employee/list", { params: { q, page_size: 12, status_filter: "Active" } });
                if (!cancelled) setEmpResults(r.data.items || []);
            } catch { /* ignore */ }
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
    }, [form.employee_query]);

    const submit = async () => {
        if (!form.device_id || !form.employee_id || !form.biometric_user_id.trim()) {
            toast.error("Device, employee and biometric_user_id are all required.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/attendance/biometric/mappings", {
                device_id: form.device_id,
                employee_id: form.employee_id,
                biometric_user_id: form.biometric_user_id.trim(),
                notes: form.notes.trim(),
            });
            toast.success("Mapping created.");
            // Check for pending unmapped events on (device, bio_user_id).
            try {
                const r = await api.get("/attendance/biometric/unmapped/pending-count", {
                    params: {
                        device_id: form.device_id,
                        biometric_user_id: form.biometric_user_id.trim(),
                    },
                });
                const pending = r.data?.pending_count || 0;
                if (pending > 0) {
                    setReprocessPrompt({
                        device_id: form.device_id,
                        biometric_user_id: form.biometric_user_id.trim(),
                        employee_id: form.employee_id,
                        pending,
                    });
                    return; // keep dialog open until admin decides
                }
            } catch { /* silent */ }
            onCreated();
        } catch (err) {
            toast.error(formatApiError(err) || "Create mapping failed");
        } finally { setSaving(false); }
    };

    const runReprocess = async () => {
        // Fetch pending event ids for this (device, bio_user_id) then resolve.
        try {
            const list = await api.get("/attendance/biometric/unmapped", {
                params: {
                    device_id: reprocessPrompt.device_id,
                    biometric_user_id: reprocessPrompt.biometric_user_id,
                    limit: 500,
                },
            });
            const ids = (list.data.items || [])
                .filter((u) => u.resolution_status === "held_unmapped" || u.resolution_status === "held_maintenance")
                .map((u) => u.id);
            if (ids.length === 0) {
                toast("No pending events to reprocess.");
                setReprocessPrompt(null);
                onCreated();
                return;
            }
            const r = await api.post("/attendance/biometric/unmapped/resolve", {
                unmapped_event_ids: ids,
                employee_id: reprocessPrompt.employee_id,
            });
            toast.success(`Reprocessed ${r.data.resolved_count} event(s). AAS ${r.data.aas_action_id || "—"}.`);
            setReprocessPrompt(null);
            onCreated();
        } catch (err) {
            toast.error(formatApiError(err) || "Reprocess failed");
        }
    };

    return (
        <>
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-lg" data-testid="dialog-create-mapping">
                <DialogHeader>
                    <DialogTitle>New biometric mapping</DialogTitle>
                    <DialogDescription>
                        Link a physical enrolment on a device to a REEDOS employee.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-3">
                    <div>
                        <Label>Device *</Label>
                        <select className="h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm"
                            value={form.device_id}
                            onChange={(e) => setForm({ ...form, device_id: e.target.value })}
                            data-testid="select-mapping-device">
                            <option value="">Choose a device…</option>
                            {devices.filter((d) => d.status !== "retired").map((d) => (
                                <option key={d.id} value={d.id}>{d.device_code} · {d.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label>Biometric user id *</Label>
                        <Input value={form.biometric_user_id}
                            onChange={(e) => setForm({ ...form, biometric_user_id: e.target.value })}
                            placeholder="as configured on the device"
                            data-testid="input-mapping-biometric-user-id" />
                    </div>
                    <div>
                        <Label>Employee *</Label>
                        <Input value={form.employee_query}
                            onChange={(e) => setForm({ ...form, employee_query: e.target.value, employee_id: "" })}
                            placeholder="Search by name / code / email…"
                            data-testid="input-mapping-employee-query" />
                        {empResults.length > 0 && (
                            <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white">
                                {empResults.map((e) => (
                                    <div key={e.id}
                                        className={`px-2 py-1.5 text-sm cursor-pointer hover:bg-slate-50 ${form.employee_id === e.id ? "bg-indigo-50" : ""}`}
                                        onClick={() => setForm({
                                            ...form,
                                            employee_id: e.id,
                                            employee_query: `${e.employee_code || ""} · ${e.full_name}`,
                                        })}
                                        data-testid={`emp-suggest-${e.employee_code || e.id}`}>
                                        <div className="font-medium">{e.full_name}</div>
                                        <div className="text-xs text-slate-500">{e.employee_code || e.id}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div>
                        <Label>Notes</Label>
                        <Input value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            data-testid="input-mapping-notes" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving}
                        data-testid="btn-submit-mapping">
                        {saving ? "Creating…" : "Create mapping"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        {reprocessPrompt && (
            <Dialog open onOpenChange={() => { setReprocessPrompt(null); onCreated(); }}>
                <DialogContent data-testid="dialog-reprocess-prompt">
                    <DialogHeader>
                        <DialogTitle>Reprocess {reprocessPrompt.pending} pending event(s)?</DialogTitle>
                        <DialogDescription>
                            The mapping is live. There are {reprocessPrompt.pending} historical
                            biometric event(s) waiting in the quarantine inbox for this
                            (device, biometric_user_id). Reprocessing routes them through
                            the canonical attendance pipeline. This is an explicit administrative
                            action recorded in the audit trail.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline"
                            onClick={() => { setReprocessPrompt(null); onCreated(); }}
                            data-testid="btn-reprocess-skip">
                            Skip · leave in inbox
                        </Button>
                        <Button onClick={runReprocess} data-testid="btn-reprocess-confirm">
                            Reprocess now
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        )}
        </>
    );
}


function BulkMappingImportDialog({ onClose, onCommitted }) {
    const [file, setFile] = useState(null);
    const [report, setReport] = useState(null);
    const [dryRunning, setDryRunning] = useState(false);
    const [committing, setCommitting] = useState(false);
    const [reason, setReason] = useState("");

    const downloadTemplate = async () => {
        try {
            const resp = await fetch(`${API_BASE}/attendance/biometric/mappings/template`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = "biometric_bulk_mapping_template.csv";
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(`Template download failed: ${err.message || err}`);
        }
    };

    const runDryRun = async () => {
        if (!file) { toast.warning("Choose a CSV file first."); return; }
        setDryRunning(true);
        setReport(null);
        try {
            const fd = new FormData();
            fd.append("file", file, file.name);
            const r = await api.post("/attendance/biometric/mappings/bulk/dry-run", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setReport(r.data.report);
            const s = r.data.report;
            if (s.block_count > 0) toast.error(`${s.block_count} blocking row(s). Fix and re-upload.`);
            else if (s.warning_count > 0) toast.warning(`${s.warning_count} warning(s). Review before commit.`);
            else toast.success(`Dry-run OK — ${s.create_count} create · ${s.noop_count} noop.`);
        } catch (err) {
            toast.error(formatApiError(err) || "Dry-run failed");
        } finally { setDryRunning(false); }
    };

    const runCommit = async () => {
        if (!report || report.block_count > 0) return;
        if (reason.trim().length < 10) {
            toast.error("Commit reason must be at least 10 characters.");
            return;
        }
        setCommitting(true);
        try {
            const r = await api.post("/attendance/biometric/mappings/bulk/commit", {
                report, reason: reason.trim(),
            });
            toast.success(`Committed — ${r.data.created_count} created · ${r.data.noop_count} noop · ${r.data.failed_count} failed. AAS ${r.data.aas_action_id || "—"}.`);
            onCommitted();
        } catch (err) {
            toast.error(formatApiError(err) || "Commit failed");
        } finally { setCommitting(false); }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-3xl" data-testid="dialog-bulk-import">
                <DialogHeader>
                    <DialogTitle>Bulk mapping · CSV import</DialogTitle>
                    <DialogDescription>
                        Upload a CSV of enrolments. Dry-Run first, then explicit Commit.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <Button variant="outline" size="sm" onClick={downloadTemplate}
                            data-testid="btn-download-template">
                            <DownloadSimple className="h-4 w-4 mr-1.5" /> Download template
                        </Button>
                        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer border rounded-md px-2 py-1 hover:bg-slate-50">
                            <CloudArrowUp className="h-4 w-4" />
                            <span>{file ? file.name : "Choose CSV"}</span>
                            <input type="file" accept=".csv,text/csv" className="hidden"
                                onChange={(e) => { setFile(e.target.files?.[0] || null); setReport(null); }}
                                data-testid="input-bulk-csv" />
                        </label>
                        <Button size="sm" onClick={runDryRun} disabled={!file || dryRunning}
                            data-testid="btn-dryrun">
                            {dryRunning ? "Validating…" : "Dry-Run"}
                        </Button>
                    </div>
                    {report && (
                        <>
                            <div className="grid grid-cols-4 gap-2 text-xs">
                                <div className="rounded-md border p-2 bg-slate-50">
                                    <div className="text-slate-500">Rows</div>
                                    <div className="text-lg font-semibold" data-testid="report-row-count">{report.row_count}</div>
                                </div>
                                <div className="rounded-md border p-2 bg-emerald-50 border-emerald-200">
                                    <div className="text-emerald-700">Create</div>
                                    <div className="text-lg font-semibold" data-testid="report-create-count">{report.create_count}</div>
                                </div>
                                <div className="rounded-md border p-2 bg-slate-50">
                                    <div className="text-slate-500">Noop (exists)</div>
                                    <div className="text-lg font-semibold" data-testid="report-noop-count">{report.noop_count}</div>
                                </div>
                                <div className={`rounded-md border p-2 ${report.block_count ? "bg-rose-50 border-rose-200" : "bg-slate-50"}`}>
                                    <div className={report.block_count ? "text-rose-700" : "text-slate-500"}>Blocked</div>
                                    <div className="text-lg font-semibold" data-testid="report-block-count">{report.block_count}</div>
                                </div>
                            </div>
                            <div className="max-h-80 overflow-y-auto rounded-md border border-slate-200">
                                <table className="w-full text-xs">
                                    <thead className="bg-slate-50 text-slate-600 uppercase text-[10px] tracking-wide">
                                        <tr>
                                            <th className="text-left px-2 py-1">#</th>
                                            <th className="text-left px-2 py-1">Device</th>
                                            <th className="text-left px-2 py-1">Bio user id</th>
                                            <th className="text-left px-2 py-1">Employee</th>
                                            <th className="text-left px-2 py-1">Action</th>
                                            <th className="text-left px-2 py-1">Findings</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {report.rows.map((r) => (
                                            <tr key={r.row_no} className="border-t border-slate-100">
                                                <td className="px-2 py-1">{r.row_no}</td>
                                                <td className="px-2 py-1 font-mono">{r.device_code}</td>
                                                <td className="px-2 py-1 font-mono">{r.biometric_user_id}</td>
                                                <td className="px-2 py-1">
                                                    {r.employee_code} <span className="text-slate-400">{r.employee_full_name}</span>
                                                </td>
                                                <td className="px-2 py-1">
                                                    <span className={`rounded px-1.5 py-0.5 border text-[10px] ${SEV_STYLES[r.severity]}`}>
                                                        {r.action}
                                                    </span>
                                                </td>
                                                <td className="px-2 py-1 text-slate-500">
                                                    {r.errors?.length ? <span className="text-rose-700">{r.errors.join("; ")}</span> : null}
                                                    {r.warnings?.length ? <span className="text-amber-700">{r.warnings.join("; ")}</span> : null}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div>
                                <Label>Commit reason (≥ 10 chars)</Label>
                                <Input value={reason} onChange={(e) => setReason(e.target.value)}
                                    data-testid="input-commit-reason" />
                            </div>
                        </>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={runCommit}
                        disabled={!report || report.block_count > 0 || committing || reason.trim().length < 10}
                        data-testid="btn-commit">
                        {committing ? "Committing…" : "Commit"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// ─────────────────────────────────────────────────────────────────
// Tab 3 · Unmapped Inbox
// ─────────────────────────────────────────────────────────────────

function UnmappedTab({ devices }) {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [deviceFilter, setDeviceFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [selected, setSelected] = useState(new Set());
    const [resolveOpen, setResolveOpen] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 500 };
            if (deviceFilter) params.device_id = deviceFilter;
            if (statusFilter) params.resolution_status = statusFilter;
            const [list, sum] = await Promise.all([
                api.get("/attendance/biometric/unmapped", { params }),
                api.get("/attendance/biometric/unmapped/summary"),
            ]);
            setRows(list.data.items || []);
            setSummary(sum.data);
            setSelected(new Set());
        } catch (err) {
            toast.error(formatApiError(err) || "Failed to load unmapped inbox");
        } finally { setLoading(false); }
    }, [deviceFilter, statusFilter]);

    useEffect(() => { refresh(); }, [refresh]);

    const toggle = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    };
    const toggleAll = () => {
        if (selected.size === rows.length) setSelected(new Set());
        else setSelected(new Set(rows.map((r) => r.id)));
    };

    return (
        <div className="space-y-4" data-testid="tab-unmapped">
            {summary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="unmapped-summary">
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                        <div className="text-[11px] uppercase tracking-wide text-amber-700">Held (unmapped)</div>
                        <div className="text-lg font-semibold" data-testid="summary-held-unmapped">{summary.held_unmapped}</div>
                    </div>
                    <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2">
                        <div className="text-[11px] uppercase tracking-wide text-sky-700">Held (maintenance)</div>
                        <div className="text-lg font-semibold" data-testid="summary-held-maintenance">{summary.held_maintenance}</div>
                    </div>
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
                        <div className="text-[11px] uppercase tracking-wide text-emerald-700">Resolved</div>
                        <div className="text-lg font-semibold" data-testid="summary-resolved">{summary.resolved}</div>
                    </div>
                    <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2">
                        <div className="text-[11px] uppercase tracking-wide text-rose-700">Rejected</div>
                        <div className="text-lg font-semibold" data-testid="summary-rejected">{summary.rejected}</div>
                    </div>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
                <select className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm"
                    value={deviceFilter} onChange={(e) => setDeviceFilter(e.target.value)}
                    data-testid="unmapped-device-filter">
                    <option value="">All devices</option>
                    {devices.map((d) => (
                        <option key={d.id} value={d.id}>{d.device_code} · {d.name}</option>
                    ))}
                </select>
                <select className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm"
                    value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    data-testid="unmapped-status-filter">
                    <option value="">Pending only</option>
                    <option value="held_unmapped">Held (unmapped)</option>
                    <option value="held_maintenance">Held (maintenance)</option>
                    <option value="resolved">Resolved</option>
                    <option value="rejected">Rejected</option>
                </select>
                <Button variant="outline" size="sm" onClick={refresh} disabled={loading}
                    data-testid="unmapped-refresh">
                    <ArrowsClockwise className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                </Button>
                {selected.size > 0 && (
                    <div className="ml-auto flex items-center gap-2">
                        <span className="text-xs text-indigo-700 font-medium"
                            data-testid="unmapped-selected-count">
                            {selected.size} selected
                        </span>
                        <Button size="sm" onClick={() => setResolveOpen(true)}
                            data-testid="btn-open-resolve">
                            <CheckCircle className="h-4 w-4 mr-1.5" /> Resolve
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setRejectOpen(true)}
                            data-testid="btn-open-reject">
                            <XCircle className="h-4 w-4 mr-1.5" /> Reject
                        </Button>
                    </div>
                )}
            </div>

            <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                        <tr>
                            <th className="px-3 py-2 w-8">
                                <input type="checkbox"
                                    checked={rows.length > 0 && selected.size === rows.length}
                                    onChange={toggleAll} data-testid="unmapped-toggle-all" />
                            </th>
                            <th className="text-left px-3 py-2">Device</th>
                            <th className="text-left px-3 py-2">Bio user id</th>
                            <th className="text-left px-3 py-2">Event</th>
                            <th className="text-left px-3 py-2">Time</th>
                            <th className="text-left px-3 py-2">Status</th>
                        </tr>
                    </thead>
                    <tbody data-testid="unmapped-tbody">
                        {rows.length === 0 && (
                            <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500 text-sm">
                                Nothing to review. All caught up.
                            </td></tr>
                        )}
                        {rows.map((u) => {
                            const isPending = u.resolution_status === "held_unmapped"
                                || u.resolution_status === "held_maintenance";
                            return (
                                <tr key={u.id}
                                    className="border-t border-slate-100 hover:bg-slate-50"
                                    data-testid={`unmapped-row-${u.id}`}>
                                    <td className="px-3 py-2">
                                        <input type="checkbox"
                                            disabled={!isPending}
                                            checked={selected.has(u.id)}
                                            onChange={() => toggle(u.id)}
                                            data-testid={`unmapped-select-${u.id}`} />
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs">{u.device_code}</td>
                                    <td className="px-3 py-2 font-mono text-xs">{u.biometric_user_id}</td>
                                    <td className="px-3 py-2 text-xs">{u.event_type}</td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(u.event_time)}</td>
                                    <td className="px-3 py-2">
                                        <StatusBadge value={u.resolution_status}
                                            styles={RESOLUTION_STATUS_STYLES}
                                            testId={`unmapped-status-${u.id}`} />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {resolveOpen && (
                <ResolveDialog ids={Array.from(selected)}
                    onClose={() => setResolveOpen(false)}
                    onDone={() => { setResolveOpen(false); refresh(); }} />
            )}
            {rejectOpen && (
                <RejectDialog ids={Array.from(selected)}
                    onClose={() => setRejectOpen(false)}
                    onDone={() => { setRejectOpen(false); refresh(); }} />
            )}
        </div>
    );
}


function ResolveDialog({ ids, onClose, onDone }) {
    const [empQuery, setEmpQuery] = useState("");
    const [empResults, setEmpResults] = useState([]);
    const [empId, setEmpId] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const q = empQuery.trim();
        if (q.length < 2) { setEmpResults([]); return; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get("/hr/employee/list", { params: { q, page_size: 12, status_filter: "Active" } });
                if (!cancelled) setEmpResults(r.data.items || []);
            } catch { /* ignore */ }
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
    }, [empQuery]);

    const submit = async () => {
        if (!empId) { toast.error("Choose an employee."); return; }
        setSaving(true);
        try {
            const r = await api.post("/attendance/biometric/unmapped/resolve", {
                unmapped_event_ids: ids,
                employee_id: empId,
            });
            const d = r.data;
            toast.success(`Resolved ${d.resolved_count} · ${d.noop_count} noop · ${d.failed_count} failed. AAS ${d.aas_action_id || "—"}.`);
            onDone();
        } catch (err) {
            toast.error(formatApiError(err) || "Resolve failed");
        } finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent data-testid="dialog-resolve">
                <DialogHeader>
                    <DialogTitle>Resolve {ids.length} event(s)</DialogTitle>
                    <DialogDescription>
                        The mapping for (device, biometric_user_id) → employee must already
                        be Active. This routes selected events through the canonical
                        attendance pipeline.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-3">
                    <div>
                        <Label>Employee *</Label>
                        <Input value={empQuery}
                            onChange={(e) => { setEmpQuery(e.target.value); setEmpId(""); }}
                            placeholder="Search by name / code / email…"
                            data-testid="input-resolve-emp-query" />
                        {empResults.length > 0 && (
                            <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white">
                                {empResults.map((e) => (
                                    <div key={e.id}
                                        className={`px-2 py-1.5 text-sm cursor-pointer hover:bg-slate-50 ${empId === e.id ? "bg-indigo-50" : ""}`}
                                        onClick={() => {
                                            setEmpId(e.id);
                                            setEmpQuery(`${e.employee_code || ""} · ${e.full_name}`);
                                        }}
                                        data-testid={`resolve-emp-suggest-${e.employee_code || e.id}`}>
                                        <div className="font-medium">{e.full_name}</div>
                                        <div className="text-xs text-slate-500">{e.employee_code || e.id}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving || !empId}
                        data-testid="btn-submit-resolve">
                        {saving ? "Resolving…" : "Resolve"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function RejectDialog({ ids, onClose, onDone }) {
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        if (reason.trim().length < 10) {
            toast.error("Reason must be at least 10 characters.");
            return;
        }
        setSaving(true);
        try {
            const r = await api.post("/attendance/biometric/unmapped/reject", {
                unmapped_event_ids: ids, reason: reason.trim(),
            });
            const d = r.data;
            toast.success(`Rejected ${d.rejected_count} · ${d.noop_count} noop · ${d.failed_count} failed. AAS ${d.aas_action_id || "—"}.`);
            onDone();
        } catch (err) {
            toast.error(formatApiError(err) || "Reject failed");
        } finally { setSaving(false); }
    };
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent data-testid="dialog-reject">
                <DialogHeader>
                    <DialogTitle>Reject {ids.length} event(s)</DialogTitle>
                    <DialogDescription>
                        Rejected events are preserved as evidence but never enter the
                        canonical attendance pipeline.
                    </DialogDescription>
                </DialogHeader>
                <div>
                    <Label>Reason (≥ 10 chars) *</Label>
                    <Textarea rows={3} value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        data-testid="input-reject-reason" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving || reason.trim().length < 10}
                        data-testid="btn-submit-reject">
                        {saving ? "Rejecting…" : "Reject"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// ─────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────

const TABS = [
    { id: "devices", label: "Devices", icon: DeviceMobile },
    { id: "mappings", label: "Mappings", icon: UserCircle },
    { id: "unmapped", label: "Unmapped Inbox", icon: WarningCircle },
];

export default function BiometricConsole() {
    const { can } = useAuth();
    const isAllowed = can?.("biometric:device:manage");

    const [tab, setTab] = useState("devices");
    const [devices, setDevices] = useState([]);
    const [devLoading, setDevLoading] = useState(false);

    const refreshDevices = useCallback(async () => {
        if (!isAllowed) return;
        setDevLoading(true);
        try {
            const r = await api.get("/attendance/biometric/devices", { params: { limit: 500 } });
            setDevices(r.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err) || "Failed to load devices");
        } finally { setDevLoading(false); }
    }, [isAllowed]);

    useEffect(() => { refreshDevices(); }, [refreshDevices]);

    if (!isAllowed) {
        return (
            <EmptyState
                title="Not authorised"
                description="The Biometric console requires the `biometric:device:manage` capability."
                data-testid="biometric-forbidden"
            />
        );
    }

    return (
        <div className="space-y-6 pb-10" data-testid="biometric-console-page">
            <PageHeader
                eyebrow="P1-C · Biometric Attendance"
                title="Biometric console"
                description={
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                        <Info className="h-3.5 w-3.5" />
                        Vendor-neutral device master, identity mappings, and quarantine inbox.
                        Ingest is authenticated per-device; only mapped, active-device punches
                        enter canonical attendance.
                    </div>
                }
            />

            <div className="px-6 sm:px-8 space-y-6">
                <div className="flex gap-2 border-b border-slate-200"
                    data-testid="biometric-tabs">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.id;
                        return (
                            <button key={t.id}
                                onClick={() => setTab(t.id)}
                                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 -mb-px transition-colors
                                    ${active ? "border-indigo-600 text-indigo-700 font-medium"
                                        : "border-transparent text-slate-600 hover:text-slate-900"}`}
                                data-testid={`tab-${t.id}`}>
                                <Icon className="h-4 w-4" />
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                {tab === "devices" && (
                    <DevicesTab devices={devices} loading={devLoading} onRefresh={refreshDevices} />
                )}
                {tab === "mappings" && (
                    <MappingsTab devices={devices} onGlobalRefresh={refreshDevices} />
                )}
                {tab === "unmapped" && (
                    <UnmappedTab devices={devices} />
                )}
            </div>
        </div>
    );
}
